import { fromJS } from 'immutable';
import R, { any, sort, insert, last, merge, concat } from 'ramda';

const initialState = fromJS({
  rateSetName: '',
  departOnDays: [],
  daysOfWeek: [],
  minStay: '',
  maxStay: '',
  commissionPercentage: undefined,
  rateType: 'includeCommission',
  includedGuestsinBaseRate: undefined,
  maxAdults: undefined,
  maxAdultsChildren: undefined,
  costBasis: '',
  retailRate: 0,
  baseRate: undefined,
  additionalAdult: undefined,
  additionalChild: undefined,
  rates: [],
  nonStandardTemplateType: 'standard',
  transfersOrPriceBreak: [{}],
  nonAccomStandard: { nett: {}, retail: {} },
  formData: {},
});

function addRateSetWizard(state = initialState, { type, payload }) {
  switch (type) {
    case 'UPDATE_FORM_DATA_SUCCESS':
      return state.update('formData', formData => formData.merge(fromJS(payload)));
    case 'GET_PRODUCT_DETAILS':
      if (payload.formData && payload.formData.currency) {
        const {
          rateType,
          additionalAdultRetail,
          additionalChildRetail,
          commissionPercentage,
        } = payload.formData;
        if (rateType === 'includeCommission') {
          const nettAdult = additionalAdultRetail && commissionPercentage ?
            Number(additionalAdultRetail - (commissionPercentage * additionalAdultRetail) / 100).toFixed(2) : undefined;
          const nettChild = additionalChildRetail && commissionPercentage ?
            Number(additionalChildRetail - (commissionPercentage * additionalChildRetail) / 100).toFixed(2) : undefined;
          return state.merge(fromJS(payload.formData))
            .set('formData', fromJS(payload.formData))
            .set('additionalAdult', nettAdult).set('additionalChild', nettChild);
        }
        return state.merge(fromJS(payload.formData))
        .set('formData', fromJS(payload.formData));
      }
      return state;
    case 'UPDATE_ADD_RATES_FIELD_WITH_KEYPATH':
      if (payload.keyPath[0] === 'transfersOrPriceBreak') {
        if (last(payload.keyPath) === 'retailRate') {
          return state.setIn(payload.keyPath, payload.value)
            .setIn(['transfersOrPriceBreak', payload.keyPath[1], 'baseRate'], Number(payload.value - payload.value * (state.get('commissionPercentage') || 100) / 100).toFixed(2));
        }
        if (last(payload.keyPath) === 'minGuests' && payload.keyPath[1]) {
          if (Number(payload.value) <= Number(state.getIn(['transfersOrPriceBreak', payload.keyPath[1] - 1, 'minGuests']))) {
            return state.setIn(payload.keyPath, Number(payload.value));
          }
          return state.setIn(['transfersOrPriceBreak', payload.keyPath[1] - 1, 'maxAdultsPlusChild'], Number(payload.value) - 1)
            .setIn(payload.keyPath, Number(payload.value));
        }
        if (state.get('nonStandardTemplateType') === 'priceBreak' && last(payload.keyPath) === 'maxAdultsPlusChild' && payload.keyPath[1] < state.get('transfersOrPriceBreak').size - 1) {
          return state.setIn(['transfersOrPriceBreak', payload.keyPath[1] + 1, 'minGuests'], Number(payload.value) + 1)
            .setIn(payload.keyPath, Number(payload.value));
        }
      }
      return state.setIn(payload.keyPath, fromJS(payload.value));
    case 'UPDATE_ADD_RATES_FIELD':
      return updateAddRatesField(state, payload);
    case 'UPDATE_ADD_RATES_DAYSOFWEEK':
      if (payload.day === 'All') {
        if (payload.checked) return state.set('daysOfWeek', fromJS(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']));
        return state.set('daysOfWeek', fromJS([]));
      }
      if (payload.checked) return state.update('daysOfWeek', daysOfWeek => daysOfWeek.push(payload.day));
      return state.deleteIn(['daysOfWeek', payload.dayIndexInReducer]);
    case 'UPDATE_ADD_RATES_RATES_TABLE_CELL_VALUE':
      if (payload.column === 'maxChild') {
        return state.updateIn(['rates', payload.row], rateDetails => {
          return rateDetails.merge(fromJS({
            maxChild: payload.value,
            maxAdultsPlusChild: parseInt(state.getIn(['rates', payload.row, 'maxAdults']), 10) + parseInt(payload.value, 10),
          }));
        });
      }
      if (payload.column === 'minGuests' && payload.row) {
        if (Number(payload.value) <= Number(state.getIn(['rates', payload.row - 1, 'minGuests']))) {
          return state.setIn(['rates', payload.row, payload.column], Number(payload.value));
        }
        return state.setIn(['rates', payload.row - 1, 'maxAdultsPlusChild'], Number(payload.value) - 1)
          .setIn(['rates', payload.row, payload.column], Number(payload.value));
      }
      if (state.get('nonStandardTemplateType') === 'priceBreak' && payload.column === 'maxAdultsPlusChild' && payload.row < state.get('rates').size - 1) {
        return state.setIn(['rates', payload.row + 1, 'minGuests'], Number(payload.value) + 1)
          .setIn(['rates', payload.row, payload.column], payload.value);
      }
      return state.setIn(['rates', payload.row, payload.column], payload.value);

    case 'CALCULATE_RATES_TABLE':
      return calRatesTableForAccommodation(state);
    case 'CALCULATE_STANDARD_NON_ACCOM_RATES_TABLE':
      if (state.get('nonStandardTemplateType') === 'multiDayTour') return calculateMultiDayTourRates(state);
      if (state.get('nonStandardTemplateType') === 'privateTransfer') return calculatePrivateTransfersRates(state);
      if (state.get('nonStandardTemplateType') === 'priceBreak') return calculatePriceBreakRates(state);
      return calculateStandardNonAccomRate(state);

    case 'SAVING_RATE_SET_TO_DB_STARTING':
      return state.set('saving', true);
    case 'CLEAR_ADD_RATE_SET_WIZARD':
    case 'CLOSE_ADD_EDIT_CONTRACT_DIALOG':
    case '{rates-draft} MANAGE_EXTRAS_OFFERS_SUCCESS':
    case 'CONTRACT_PRODUCT_UPDATED':
      return initialState.merge(fromJS({
        // location information is persistent through the location, need to update it if user update it once
        currency: state.get('currency'),
        taxInclusion: state.get('taxInclusion'),
        taxType: state.get('taxType'),
        // product information is persistent through the product, need to update it if user update it once
        includedGuestsinBaseRate: state.get('includedGuestsinBaseRate'),
        maxAdults: state.get('maxAdults'),
        maxAdultsChildren: state.get('maxAdultsChildren'),
        additionalAdult: state.get('additionalAdult'),
        additionalChild: state.get('additionalChild'),
        additionalAdultRetail: state.get('additionalAdultRetail'),
        additionalChildRetail: state.get('additionalChildRetail'),
        paxMax: state.get('paxMax'),
        twinBedsAvail: state.get('twinBedsAvail'),
        // rateType information is persistent through the draft no matter user update them in a wizard or not
        rateType: state.getIn(['formData', 'rateType']),
        commissionPercentage: state.getIn(['formData', 'commissionPercentage']),
        nettCommission: state.getIn(['formData', 'nettCommission']),
        formData: state.get('formData'),
      }));
    default:
      return state;
  }
}

const calculatePriceBreakRates = thisState => {
  const transfersOrPriceBreak = thisState.get('transfersOrPriceBreak').toJS().filter(rate => rate.minGuests && rate.baseRate);
  const rates = (() => {
    return transfersOrPriceBreak.map(priceBreak => ({
      description: 'Per Person',
      maxAdultsPlusChild: priceBreak.maxAdultsPlusChild || 999,
      minGuests: priceBreak.minGuests,
      nettRate: Number(priceBreak.baseRate).toFixed(2),
      retailRate: thisState.get('rateType') === 'nett' ? undefined : Number(priceBreak.retailRate).toFixed(2),
    }));
  })();
  return thisState.set('rates', fromJS(rates));
};

const calculatePrivateTransfersRates = thisState => {
  const transfersOrPriceBreak = thisState.get('transfersOrPriceBreak').toJS();
  const sortedTransfer = sort((a, b) => Number(a.maxAdultsPlusChild) - Number(b.maxAdultsPlusChild), transfersOrPriceBreak);
  const rates = (() => {
    return sortedTransfer.map(transfer => ({
      description: 'Up to',
      priceCode: '',
      maxAdultsPlusChild: Number(transfer.maxAdultsPlusChild),
      nettRate: Number(transfer.baseRate).toFixed(2),
      retailRate: thisState.get('rateType') === 'nett' ? undefined : Number(transfer.retailRate).toFixed(2),
    }));
  })();
  return thisState.set('rates', fromJS(rates));
};
import {
  multiDayTourDescrs,
  multiDayToursMaxAdultOne,
  multiDayToursMaxChildOne,
} from '../../../utils/contracting-preset-names-codes';
const calculateMultiDayTourRates = thisState => {
  const rateKeys = multiDayTourDescrs;

  const rates = rateKeys.map(rateKey => {
    const actualKey = rateKey.toLowerCase();
    const maxAdults = (() => {
      if (multiDayToursMaxAdultOne.indexOf(rateKey) > -1) return 1;
      return 0;
    })();
    const maxAdultsPlusChild = (() => {
      if ([...multiDayToursMaxAdultOne, ...multiDayToursMaxChildOne].indexOf(rateKey) > -1) return 1;
      return 0;
    })();
    const retailRate = !isNaN(thisState.getIn(['nonAccomStandard', 'retail', actualKey]))
      ? Number(thisState.getIn(['nonAccomStandard', 'retail', actualKey])).toFixed(2)
      : thisState.getIn(['nonAccomStandard', 'retail', actualKey]);
    const nettRate = Number(thisState.getIn(['nonAccomStandard', 'nett', actualKey])).toFixed(2);
    return {
      description: rateKey,
      maxAdults,
      maxAdultsPlusChild,
      maxChild: R.call(R.cond([
        [R.equals('extra child'), R.always(1)],
        [R.T, R.always(0)],
      ]), actualKey),
      retailRate,
      nettRate: isNaN(nettRate) ? null : nettRate,
      notApplicable: !any(value => value)([
        thisState.getIn(['nonAccomStandard', 'retail', actualKey]),
        thisState.getIn(['nonAccomStandard', 'nett', actualKey]),
      ]),
    };
  });
  return thisState.set('rates', fromJS(rates)).set('nonStandardTemplateType', 'multiDayTour');
};

const calculateStandardNonAccomRate = state => {
  const rateKeys = ['Adult', 'Child', 'Infant', 'Family', 'Senior/Concession', 'Student', 'General'];

  const rates = rateKeys.map(rateKey => {
    const actualKey = rateKey.toLowerCase().indexOf('senior') > -1 ? 'senior' : rateKey.toLowerCase();
    const maxAdults = (() => {
      if (['adult', 'senior', 'student', 'general'].indexOf(actualKey) > -1) return 1;
      if (actualKey === 'family') return 2;
      return 0;
    })();
    const maxAdultsPlusChild = (() => {
      if (['adult', 'child', 'senior', 'student', 'general'].indexOf(actualKey) > -1) return 1;
      if (actualKey === 'family') return 4;
      return 0;
    })();
    const retailRate = !isNaN(state.getIn(['nonAccomStandard', 'retail', actualKey]))
      ? Number(state.getIn(['nonAccomStandard', 'retail', actualKey])).toFixed(2)
      : state.getIn(['nonAccomStandard', 'retail', actualKey]);
    const nettRate = Number(state.getIn(['nonAccomStandard', 'nett', actualKey])).toFixed(2);
    return {
      description: rateKey,
      maxAdults,
      maxChild: R.call(R.cond([
        [R.equals('child'), R.always(1)],
        [R.equals('family'), R.always(2)],
        [R.T, R.always(0)],
      ]), actualKey),
      maxAdultsPlusChild,
      retailRate,
      nettRate: isNaN(nettRate) ? null : nettRate,
      notApplicable: !any(value => value)([
        state.getIn(['nonAccomStandard', 'retail', actualKey]),
        state.getIn(['nonAccomStandard', 'nett', actualKey]),
      ]),
    };
  });
  return state.set('rates', fromJS(rates));
};

function getRateDesc(maxAdults) {
  switch (maxAdults) {
    case 1:
      return 'Single';
    case 2:
      return 'Double';
    case 3:
      return 'Triple';
    case 4:
      return 'Quad';
    default:
      return maxAdults;
  }
}

function calRatesTableForAccommodation(thisState) {
  const includedGuestsinBaseRate = parseInt(thisState.get('includedGuestsinBaseRate'), 10);
  const isCommisionRate = thisState.get('rateType') !== 'nett';
  const baseRate = thisState.get('baseRate') ? Number(thisState.get('baseRate')) : 0;
  const additionalAdult = thisState.get('additionalAdult') ? Number(thisState.get('additionalAdult')) : 0;
  const additionalAdultRetail = thisState.get('additionalAdultRetail') ? Number(thisState.get('additionalAdultRetail')) : 0;
  const additionalChild = thisState.get('additionalChild') ? Number(thisState.get('additionalChild')) : 0;
  const additionalChildRetail = thisState.get('additionalChildRetail') ? Number(thisState.get('additionalChildRetail')) : 0;
  const maxAdults = thisState.get('maxAdults') ? parseInt(thisState.get('maxAdults'), 10) : 0;
  const maxAdultsChildren = thisState.get('maxAdultsChildren') ? parseInt(thisState.get('maxAdultsChildren'), 10) : 0;
  const rates = new Array(Math.max(4, maxAdults)).fill(1).map((_, index) => {
    const i = index + 1;
    const maxAdultsPlusChild = maxAdultsChildren;
    const nettRate = i < includedGuestsinBaseRate ? Number(baseRate).toFixed(2) : Number(baseRate + additionalAdult * (i - includedGuestsinBaseRate)).toFixed(2);
    const retailRate = (() => {
      if (!isCommisionRate) return undefined;
      if (i < includedGuestsinBaseRate) return Number(thisState.get('retailRate')).toFixed(2);
      return Number(Number(thisState.get('retailRate')) + additionalAdultRetail * (i - includedGuestsinBaseRate)).toFixed(2);
    })();
    const description = (() => {
      if (maxAdults > 4) return `${i} Guest${i > 1 ? 's' : ''}`;
      return getRateDesc(i);
    })();
    return {
      description,
      maxAdults: i,
      maxAdultsPlusChild,
      nettRate,
      retailRate,
      priceCode: '',
      notApplicable: index >= maxAdults || (i > 2 && thisState.get('paxMax') < i),
    };
  });
  const ratesWithExtra = concat(rates, [{
    description: 'Extra Adult',
    maxAdults: undefined,
    maxAdultsPlusChild: undefined,
    nettRate: additionalAdult.toFixed(2),
    retailRate: isCommisionRate ? additionalAdultRetail.toFixed(2) : undefined,
    notApplicable: true,
    priceCode: '',
  }, {
    description: 'Extra Child',
    maxAdults: undefined,
    maxAdultsPlusChild: undefined,
    nettRate: additionalChild.toFixed(2),
    retailRate: isCommisionRate ? additionalChildRetail.toFixed(2) : undefined,
    notApplicable: (() => {
      if (additionalChild) {
        if (additionalChild !== additionalAdult) return false;
      }
      if (additionalChildRetail) {
        if (additionalChildRetail !== additionalAdultRetail) return false;
      }
      return maxAdults === maxAdultsChildren;
    })(),
    priceCode: '',
  }, {
    description: 'Extra Infant',
    maxAdults: undefined,
    maxAdultsPlusChild: undefined,
    nettRate: undefined,
    retailRate: undefined,
    notApplicable: true,
    priceCode: '',
  }]);
  const ratesToSave = fromJS(insert(2, merge(rates[1], { description: 'Twin', notApplicable: maxAdults > 4 || maxAdults < 2 || thisState.get('paxMax') < 2 || !thisState.get('twinBedsAvail') }), ratesWithExtra));

  return thisState.set('rates', ratesToSave);
}

const updateAddRatesField = (state, payload) => {
  if (payload.nonAccomStandard) {
    if (payload.retail) {
      return state.setIn(['nonAccomStandard', 'retail', payload.fieldName], payload.value)
        .setIn(['nonAccomStandard', 'nett', payload.fieldName], Number(payload.value - (payload.value * (state.get('commissionPercentage') || 100)) / 100).toFixed(2));
    }
    return state.setIn(['nonAccomStandard', 'nett', payload.fieldName], payload.value);
  }
  const retailNettRateMapForAccommo = {
    retailRate: 'baseRate',
    additionalAdultRetail: 'additionalAdult',
    additionalChildRetail: 'additionalChild',
  };
  if (retailNettRateMapForAccommo[payload.fieldName] && payload.value) {
    return state.set(payload.fieldName, payload.value)
      .set(retailNettRateMapForAccommo[payload.fieldName], Number(payload.value - (payload.value * (state.get('commissionPercentage') || 100)) / 100).toFixed(2));
  }
  if (payload.fieldName === 'commissionPercentage' && payload.value) {
    const retailRates = state.getIn(['nonAccomStandard', 'retail']).toJS();
    const keys = Object.keys(retailRates);
    const nettRates = keys.length ? keys.reduce((rates, key) => {
      const retailRate = retailRates[key] || 0;
      rates[key] = Number(retailRate - (retailRate * payload.value) / 100).toFixed(2);
      return rates;
    }, {}) : {};
    const additionalAdultRetail = state.get('additionalAdultRetail') || 0;
    const additionalChildRetail = state.get('additionalChildRetail') || 0;
    return state.set(payload.fieldName, payload.value)
      .set('baseRate', Number(state.get('retailRate') - (payload.value * state.get('retailRate')) / 100).toFixed(2))
      .set('additionalAdult', Number(additionalAdultRetail - (payload.value * additionalAdultRetail) / 100).toFixed(2))
      .set('additionalChild', Number(additionalChildRetail - (payload.value * additionalChildRetail) / 100).toFixed(2))
      .setIn(['nonAccomStandard', 'nett'], fromJS(nettRates))
      .update('transfersOrPriceBreak', transfers => {
        return transfers.map(transfer => transfer.set('baseRate', Number(transfer.get('retailRate') - (payload.value * transfer.get('retailRate')) / 100).toFixed(2)));
      });
  }
  if (payload.fieldName === 'maxAdults') {
    return state.merge(fromJS({
      maxAdults: payload.value,
      // maxAdultsError: payload.value < state.get('includedGuestsinBaseRate') ? 'This value cannot be less than included guests.' : '',
    }));
  }
  if (payload.fieldName === 'maxAdultsChildren') {
    return state.merge(fromJS({
      maxAdultsChildren: payload.value,
      // maxGuestsError: payload.value < state.get('maxAdults') ? 'This value cannot be less than maximum adults.' : '',
    }));
  }
  if (payload.fieldName === 'includedGuestsinBaseRate') {
    return state.merge(fromJS({
      includedGuestsinBaseRate: payload.value,
      // includedGuestsError: payload.value > state.get('maxAdults') ? 'This value cannot be more than maximum adults.' : '',
    }));
  }
  if (payload.fieldName === 'rateType') {
    return state.merge(fromJS({
      rateType: payload.value,
      retailRate: '',
      baseRate: '',
      additionalAdult: '',
      additionalAdultRetail: '',
      additionalChild: '',
      additionalChildRetail: '',
      nettCommission: undefined,
      commissionPercentage: undefined,
      nonAccomStandard: { nett: {}, retail: {} },
    }))
      .set('transfersOrPriceBreak', state.get('transfersOrPriceBreak').map(transfer => transfer.set('baseRate', 0).set('retailRate', 0)));
  }
  if (payload.fieldName === 'seasonName') return state;
  if (payload.fieldName === 'stayPays') return state.set('stayPays', fromJS(payload.value));
  return state.set(payload.fieldName, payload.value);
};

export default addRateSetWizard;



// WEBPACK FOOTER //
// ./src/app/stores/contracting/add-rates-wizard/add-rate-set-wizard-reducer.js